import { ValidatorError, Option } from "./validation";

export class OptionFieldValidator {
  constructor(
    private options: Option[] | (() => Option[]),
    private getHiddenValue: () => string,
    private isMultiselect: boolean = false,
  ) {}

  /**
   * Validates visible input for option-based components (multiselect, typeahead)
   * Core rule: Either select from dropdown or clear field - no partial matches allowed
   */
  validateVisibleInput(visibleValue: string | null): ValidatorError | null {
    const visibleText = visibleValue ? visibleValue.trim() : "";
    const hiddenValue = this.getHiddenValue();
    const options = this.getOptions();

    if (visibleText.length === 0) {
      return null;
    }
    const matchesValidOption = options.some((option) => {
      const displayValue =
        typeof option.display === "string"
          ? option.display
          : String(option.display || "");
      return displayValue.toLowerCase().trim() === visibleText.toLowerCase();
    });

    if (!matchesValidOption) {
      return {
        message:
          "Please select an option from the dropdown options or clear the field",
      };
    }

    return null;
  }

  /**
   * Validates required state for option-based components
   * For multiselect: requires at least one selection (hidden value) regardless of visible input
   * For typeahead: requires a selection (hidden value) regardless of visible input
   */
  validateRequired(
    visibleValue: string | null,
    fieldName: string,
  ): ValidatorError | null {
    if (!this.hasValidSelections(visibleValue)) {
      return {
        message: `${fieldName} is required`,
      };
    }

    return null;
  }

  private hasValidSelections(visibleValue: string | null): boolean {
    const hiddenValue = this.getHiddenValue();
    const hasSelections = hiddenValue && hiddenValue.trim().length > 0;
    const visibleText = visibleValue ? visibleValue.trim() : "";

    return hasSelections || visibleText.length > 0;
  }

  private getOptions(): Option[] {
    return typeof this.options === "function" ? this.options() : this.options;
  }
}
